import { useCallback } from 'react';
import { LAYER_IDS, LAYER_CONFIG_MAP } from '../config/mapConfig';
import {
  COORDAL_NATURAL_LABEL_SOURCE_ID,
  getLayerIdsForSources,
  isMapStyleReady,
} from '../../../utils/mapLayerPromotion';

/** Resolve 2D/3D Ghana building layer visibility from the two UI toggles. */
export function resolveBuildingLayerVisibility(buildings: boolean, buildings3D: boolean) {
  if (!buildings) {
    return {
      show2d: false,
      show3d: false,
      showUnderground: false,
    };
  }
  if (buildings3D) {
    return {
      show2d: false,
      show3d: true,
      showUnderground: false,
    };
  }
  return {
    show2d: true,
    show3d: false,
    showUnderground: false,
  };
}

/** Mapbox Standard config — enables 3D tree models (ghana-trees-model); kept on in code, not in UI. */
export function enableMapbox3dModels(map: import('mapbox-gl').Map | null) {
  try {
    (map as any)?.setConfigProperty('basemap', 'show3dObjects', true);
  } catch {
    // Config property may not be available on all styles
  }
}

export function useMapLayers(mapRef: React.MutableRefObject<import('mapbox-gl').Map | null>) {

  const setVis = useCallback((layerId: string, visible: boolean) => {
    try {
      if (mapRef.current?.getLayer(layerId)) {
        mapRef.current.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      }
    } catch (e) {
      console.warn(`Could not toggle layer ${layerId}:`, e);
    }
  }, [mapRef]);

  const setConfig = useCallback((prop: string, value: boolean | number | string) => {
    try {
      (mapRef.current as any)?.setConfigProperty('basemap', prop, value);
    } catch (e) {
      console.warn(`Could not set config property ${prop}:`, e);
    }
  }, [mapRef]);

  const toggleGroup = useCallback((group: string, enabled: boolean) => {
    const ids = LAYER_IDS[group];
    if (!ids) return;
    ids.forEach(id => setVis(id, enabled));
  }, [setVis]);

  const toggleBySources = useCallback((sources: readonly string[], enabled: boolean) => {
    const map = mapRef.current;
    if (!map || !isMapStyleReady(map)) return;
    for (const layerId of getLayerIdsForSources(map, sources)) {
      setVis(layerId, enabled);
    }
  }, [mapRef, setVis]);

  const applyBuildingLayers = useCallback((buildings: boolean, buildings3D: boolean) => {
    const vis = resolveBuildingLayerVisibility(buildings, buildings3D);
    setVis('2d-building', vis.show2d);
    setVis('2d-building-outline-ghana', vis.show2d);
    setVis('3d-building', vis.show3d);
    setVis('building-underground', vis.showUnderground);
    enableMapbox3dModels(mapRef.current);
  }, [mapRef, setVis]);

  const toggleLayer = useCallback((layer: string, enabled: boolean) => {
    if (!mapRef.current) return;

    if (layer === 'buildings' || layer === 'buildings3D') {
      return;
    }

    const layerIds = LAYER_IDS[layer];
    if (!layerIds) return;

    if (layer === 'roads') {
      toggleGroup('roads', enabled);
      toggleGroup('tunnels', enabled);
      toggleGroup('bridges', enabled);
      setConfig('showPedestrianRoads', enabled);
      return;
    }

    const configProp = LAYER_CONFIG_MAP[layer];
    if (configProp) {
      setConfig(configProp, enabled);
    }

    if (layer === 'landcover') {
      toggleGroup('landcover', enabled);
      toggleBySources(['ghana-landcover'], enabled);
      return;
    }

    if (layer === 'landuse') {
      const map = mapRef.current;
      toggleGroup('landuse', enabled);
      if (map && isMapStyleReady(map)) {
        for (const layerId of getLayerIdsForSources(map, ['ghana-landuse'])) {
          if (layerId === 'national-park' || layerId === 'pitch-outline') continue;
          setVis(layerId, enabled);
        }
      } else {
        setVis('landuse', enabled);
        setVis('recreation-polygon', enabled);
      }
      setVis('pitch-outline', false);
      return;
    }

    if (layer === 'nationalParks') {
      setVis('national-park', enabled);
      return;
    }

    if (layer === 'placeLabels') {
      setConfig('showPlaceLabels', enabled);
      toggleGroup('placeLabels', enabled);
      toggleGroup('naturalLabels', enabled);
      toggleBySources([COORDAL_NATURAL_LABEL_SOURCE_ID], enabled);
      return;
    }

    if (layer === 'settlementLabels') {
      toggleGroup('settlementLabels', enabled);
      return;
    }

    if (layer === 'poiLabels') {
      setConfig('showPointOfInterestLabels', enabled);
      toggleGroup('poiLabels', enabled);
      toggleBySources(['ghana-pois', 'ghana-landmarks'], enabled);
      return;
    }

    if (layer === 'roadLabels') {
      setConfig('showRoadLabels', enabled);
      toggleGroup('roadLabels', enabled);
      return;
    }

    if (layer === 'transportStops') {
      toggleGroup('transportStops', enabled);
      return;
    }

    if (layer === 'trafficSignals') {
      toggleGroup('trafficSignals', enabled);
      toggleBySources(['ghana-traffic-signals'], enabled);
      return;
    }

    if (layer === 'trees') {
      toggleGroup('trees', enabled);
      toggleBySources(['ghana-trees'], enabled);
      return;
    }

    if (layer === 'structures') {
      toggleGroup('structures', enabled);
      return;
    }

    if (layer === 'airportLabels') {
      setVis('airport-label', enabled);
      return;
    }

    if (layer === 'motorwayExits') {
      setVis('road-exit-shield', enabled);
      return;
    }

    if (layer === 'buildingLabels') {
      toggleGroup('buildingLabels', enabled);
      toggleBySources(['ghana-building-numbers'], enabled);
      return;
    }

    if (layer === 'country') {
      toggleGroup('country', enabled);
      toggleBySources(['ghana-world-countries', 'ghana-admin-boundaries'], enabled);
      return;
    }

    if (layer === 'regions') {
      toggleGroup('regions', enabled);
      toggleBySources(['ghana-admin-boundaries'], enabled);
      return;
    }

    if (layer === 'districts') {
      toggleGroup('districts', enabled);
      toggleBySources(['ghana-admin-boundaries'], enabled);
      return;
    }

    if (layer === 'serviceAreas') {
      toggleGroup('serviceAreas', enabled);
      toggleBySources(['ghana-service-areas'], enabled);
      return;
    }

    if (layer === 'hillshade') {
      toggleGroup('hillshade', enabled);
      toggleBySources(['ghana-hillshade'], enabled);
      return;
    }

    if (layer === 'contours') {
      toggleGroup('contours', enabled);
      toggleBySources(['ghana-contours'], enabled);
      return;
    }

    layerIds.forEach(id => setVis(id, enabled));
  }, [mapRef, setVis, setConfig, toggleGroup, toggleBySources]);

  const toggle3D = useCallback((enabled: boolean) => {
    if (!mapRef.current) return;
    applyBuildingLayers(true, enabled);
  }, [applyBuildingLayers]);

  const toggleTerrain = useCallback((enabled: boolean, exaggeration: number = 1.5) => {
    if (!mapRef.current) return;
    if (enabled) {
      if (mapRef.current.getSource('ghana-terrain-rgb')) {
        mapRef.current.setTerrain({ source: 'ghana-terrain-rgb', exaggeration });
      }
    } else {
      mapRef.current.setTerrain(null);
    }
  }, [mapRef]);

  const setTerrainExaggeration = useCallback((exaggeration: number) => {
    if (!mapRef.current) return;
    const terrain = mapRef.current.getTerrain();
    if (terrain) {
      mapRef.current.setTerrain({ ...terrain, exaggeration });
    }
  }, [mapRef]);

  return {
    toggleLayer,
    applyBuildingLayers,
    enableMapbox3dModels: () => enableMapbox3dModels(mapRef.current),
    toggle3D,
    toggleTerrain,
    setTerrainExaggeration,
  };
}
